import React, { useState, useEffect, useMemo } from 'react';
import {
  Sparkles, FileText, Download, ExternalLink, ShieldCheck, CheckCircle2,
  AlertCircle, ArrowLeft, Upload, X, Building2, Scale, Calculator,
  Radio, FileCheck, Copy, Check, BookOpen, Layers, Zap, CheckCircle, RefreshCw,
  Search, Filter, Send, MessageSquare, History, Users, UserCheck
} from 'lucide-react';

interface NcuvkzUniversalConsultantViewProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  currentUserEmail?: string;
  currency?: string;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
  enterpriseId?: string;
}

interface NormativeReference {
  title: string;
  shortCode: string;
  article?: string;
  url: string;
  relevance: string;
}

interface RoleUsed {
  id: string;
  name: string;
  type?: 'platform_agent' | 'vault_prompt';
  domain: string;
  summary: string;
  category?: string;
}

interface SpecialistAnalysis {
  roleId: string;
  roleName: string;
  type?: 'platform_agent' | 'vault_prompt';
  domain: string;
  promptUsed: string;
  keyPoints: string[];
  analysis: string;
}

interface FollowUp {
  question: string;
  answer: string;
  rolesInvolved?: string[];
  timestamp: string;
}

interface CourtPrecedent {
  caseNumber: string;
  proceedingNumber?: string;
  courtName: string;
  decisionDate: string;
  legalPosition: string;
  url: string;
  relevance: string;
}

interface UniversalConsultantResult {
  sessionId?: string;
  docNumber: string;
  docDate: string;
  question: string;
  executiveSummary: string;
  rolesUsed: RoleUsed[];
  specialistAnalyses: SpecialistAnalysis[];
  normativeReferences: NormativeReference[];
  courtPrecedents?: CourtPrecedent[];
  actionPlan: string[];
  riskMatrix: Array<{ risk: string; severity: 'high' | 'medium' | 'low'; mitigation: string }>;
  fullReportMarkdown: string;
  followUps?: FollowUp[];
  docxUrl: string;
  downloadDocxUrl?: string;
  pdfUrl?: string;
  downloadPdfUrl?: string;
  modelUsed: string;
  executionTimeMs: number;
  isCached?: boolean;
  cacheSimilarity?: number;
}

interface EcosystemAgent {
  id: string;
  name: string;
  hub: string;
  description: string;
  type: 'platform_agent';
}

interface EcosystemPrompt {
  id: string;
  name: string;
  category: string;
  description: string;
  type: 'vault_prompt';
}

interface EcosystemCatalog {
  totalAgents: number;
  totalPrompts: number;
  totalCapabilities: number;
  hubs: string[];
  categories: string[];
  agents: EcosystemAgent[];
  prompts: EcosystemPrompt[];
}

const AVAILABLE_SPECIALISTS = [
  { id: 'remote_sensing', labelUk: 'Фахівець ДЗЗ & Геодані', labelEn: 'Remote Sensing Expert', icon: Radio },
  { id: 'mission_control', labelUk: 'Інженер ЦУП & СКАКО', labelEn: 'Mission Control Engineer', icon: Layers },
  { id: 'gcsk_special_control', labelUk: 'Спеціаліст ГЦСК (Сейсмоакустика)', labelEn: 'GCSK Geophysics Expert', icon: Zap },
  { id: 'accounting', labelUk: 'Головний бухгалтер & КЕКВ', labelEn: 'Chief Accountant', icon: Calculator },
  { id: 'legal', labelUk: 'Юрисконсульт (ЦКУ / ЗУ № 4196-IX)', labelEn: 'Legal Counsel', icon: Scale },
  { id: 'procurement', labelUk: 'Публічні закупівлі (Prozorro)', labelEn: 'Public Procurement', icon: FileCheck },
  { id: 'hr_labor', labelUk: 'Кадри & Відрядження (КЗпП)', labelEn: 'HR & Labor Code', icon: Building2 },
  { id: 'cyber_secrecy', labelUk: 'Кіберзахист & ТЗІ', labelEn: 'Cybersecurity & InfoSec', icon: ShieldCheck },
  { id: 'secretariat', labelUk: 'Секретар (ДСТУ 4163:2020)', labelEn: 'Secretariat & DSTU', icon: FileText }
];

const PRESETS = [
  {
    titleUk: "🛰️ ДЗЗ: Закупівля даних супутникового моніторингу за КЕКВ",
    descUk: "Визначення КЕКВ (2240/2282/3110), вимоги до ліцензійного договору з іноземним оператором та ризики аудиту ДАСУ.",
    question: "Необхідно провести закупівлю супутникових знімків надвисокої просторової розрізненості для потреб екологічного моніторингу та оборони. Який код КЕКВ застосовувати (2240 чи 2282)? Які істотні умови повинен містити ліцензійний договір на передачу геопросторових даних, та як запобігти зауваженням Держаудитслужби?"
  },
  {
    titleUk: "🧭 ГЦСК: Організація цілодобового чергування та відрядження на сейсмостанцію",
    descUk: "Оформлення чергування інженерного складу, надбавки за нічний час (КЗпП) та виплата добових за Постановою № 98.",
    question: "Підрозділ ГЦСК направляє групу інженерів у термінове відрядження для калібрування інфразвукового датчика на віддаленій станції з організацією цілодобового чергування. Який порядок оформлення наказу за ДСТУ 4163, які норми оплати нічних годин за КЗпП та які нормативи добових діють за Постановою КМУ № 98?"
  },
  {
    titleUk: "🛡️ ТЗІ: Порядок розсекречення та передачі матеріалів супутникової розвідки",
    descUk: "Дотримання Закону «Про держтаємницю», порядок зняття грифу та підготовка супровідного листа до Міндовкілля.",
    question: "Центр отримав запит від Міндовкілля щодо надання супутникових даних моніторингу зони надзвичайної ситуації. Дані містять інформацію подвійного використання. Як правильно провести експертизу щодо наявності держтаємниці, оформити рішення експерта та супровідний лист за ДСТУ 4163:2020?"
  },
  {
    titleUk: "⚖️ Господарський договір: Претензійна робота через зрив строків модернізації ЦУП",
    descUk: "Підготовка претензії до підрядника, нарахування пені та договірної неустойки за ЦКУ, збереження гарантійних зобов'язань.",
    question: "Підрядник прострочив на 45 днів постачання телеметричного обладнання для модернізації пункту прийому інформації. Як скласти обґрунтовану претензію з нарахуванням штрафних санкцій за нормами ЦКУ (ст. 549, 611) та зберегти дію основного договору?"
  }
];

export default function NcuvkzUniversalConsultantView({
  language,
  onBack,
  onGoHome,
  currentUserEmail,
  useEnterpriseContext,
  enterpriseName: initialEnterpriseName,
  enterpriseId
}: NcuvkzUniversalConsultantViewProps) {
  const isUk = language === 'uk';
  const isEn = language === 'en';

  const [question, setQuestion] = useState('');
  const [selectedSpecialists, setSelectedSpecialists] = useState<string[]>([]);
  const [includeKnowledgeBase, setIncludeKnowledgeBase] = useState(true);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  // Ecosystem Catalog State (368 agents + 96 prompts = 464 total)
  const [catalog, setCatalog] = useState<EcosystemCatalog | null>(null);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogTypeTab, setCatalogTypeTab] = useState<'all' | 'platform_agent' | 'vault_prompt'>('all');
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string>('all');

  // Async & Download State
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<UniversalConsultantResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Telegram .md only state
  const [sendingTgMd, setSendingTgMd] = useState(false);
  const [tgMdSuccess, setTgMdSuccess] = useState(false);

  // Follow-up clarifying chat state
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);

  // Consultation History state
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Interactive Roundtable state
  const [focusedSpecialistId, setFocusedSpecialistId] = useState<string | null>(null);

  // Fetch complete ecosystem catalog and past history on component mount
  useEffect(() => {
    fetch('/api/enterprise/universal-consultant/ecosystem-catalog')
      .then(res => {
        if (!res.ok) throw new Error("Catalog fetch failed");
        return res.json();
      })
      .then(data => {
        setCatalog(data);
      })
      .catch(err => {
        console.warn("Could not load ecosystem catalog:", err);
      });

    handleLoadHistory();
  }, []);

  const STEPS = [
    isUk ? "Вилучення та семантичний аналіз змісту вхідних файлів (.docx, .xlsx, .pdf)..." : "Ingesting and parsing input file text...",
    isUk ? "Пошук та мобілізація спеціалістів серед 368 ШІ-агентів та 96 промптів екосистеми..." : "Matching and mobilizing experts from 464 platform capabilities...",
    isUk ? "Колегіальний синтез на ШІ-моделі Gemini 3.7 Flash: зіставлення статей законів та кодексів..." : "Synthesizing consensus on Gemini 3.7 Flash engine...",
    isUk ? "Верифікація активних посилань на офіційну базу законодавства ВРУ (zakon.rada.gov.ua)..." : "Verifying statutory hyperlinks to zakon.rada.gov.ua...",
    isUk ? "Рендеринг офіційного висновку за ДСТУ 4163:2020 у MS Word (.docx) та PDF..." : "High-fidelity rendering onto letterhead in Word & PDF..."
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAnalyzing) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep(prev => (prev < STEPS.length - 1 ? prev + 1 : prev));
      }, 4500);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAnalyzing]);

  const toggleSpecialist = (id: string) => {
    setSelectedSpecialists(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (idx: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  // Robust file downloader: triggers dedicated download endpoint, converts to blob, clicks invisible link
  const handleDownloadFile = async (fileUrl: string | undefined, defaultFilename: string, isPdf: boolean = false) => {
    if (!fileUrl) return;
    const baseName = fileUrl.split("/").pop() || defaultFilename;
    const downloadApiUrl = fileUrl.startsWith("/api/enterprise/universal-consultant/download/")
      ? fileUrl
      : `/api/enterprise/universal-consultant/download/${encodeURIComponent(baseName)}`;

    if (isPdf) setDownloadingPdf(true);
    else setDownloadingDocx(true);

    try {
      const response = await fetch(downloadApiUrl);
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = defaultFilename || baseName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
        return;
      }
    } catch (e) {
      console.warn("Direct blob fetch failed, trying direct window open:", e);
    } finally {
      if (isPdf) setDownloadingPdf(false);
      else setDownloadingDocx(false);
    }

    // Direct fallback
    window.open(fileUrl, "_blank");
  };

  const handleRunConsultation = async (forceFresh = false) => {
    const handleConsult = handleRunConsultation;
    if (!question.trim()) {
      setError(isUk ? "Будь ласка, введіть ваше питання або предмет консультації." : "Please enter your question.");
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    if (forceFresh) {
      setResult(null);
    }

    try {
      const formData = new FormData();
      formData.append("question", question);
      formData.append("enterpriseId", enterpriseId || "c54dc9c1-00e1-4620-b922-4d6572f9662b");
      formData.append("userEmail", currentUserEmail || "bass776@ukr.net");
      formData.append("includeKnowledgeBase", String(includeKnowledgeBase));
      formData.append("language", language);
      if (forceFresh) {
        formData.append("forceFresh", "true");
      }

      if (selectedSpecialists.length > 0) {
        formData.append("selectedSpecialistIds", JSON.stringify(selectedSpecialists));
      }

      attachedFiles.forEach(file => {
        formData.append("files", file);
      });

      const res = await fetch("/api/enterprise/universal-consultant/analyze", {
        method: "POST",
        body: formData
      });

      if (!res.ok) {
        let errorMsg = "";
        try {
          const errData = await res.json();
          errorMsg = errData.error || errData.message;
        } catch (_) {
          const raw = await res.text().catch(() => "");
          if (raw && raw.length < 200) errorMsg = raw;
        }
        throw new Error(errorMsg || (isUk ? "Помилка роботи Універсального Консультанта." : "Consultation failed."));
      }

      const data: UniversalConsultantResult = await res.json();
      setResult(data);
      setFollowUps(data.followUps || []);
      handleLoadHistory();
    } catch (err: any) {
      console.error("Consultation error:", err);
      setError(err.message || (isUk ? "Виникла помилка під час обробки запиту." : "An error occurred."));
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Send ONLY .md document to Telegram (@Antigravity_cli_contabo_bot)
  const handleSendTelegramMd = async () => {
    if (!result) return;
    setSendingTgMd(true);
    setTgMdSuccess(false);

    try {
      const res = await fetch("/api/enterprise/universal-consultant/send-telegram", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          docNumber: result.docNumber,
          docDate: result.docDate,
          question: result.question,
          executiveSummary: result.executiveSummary,
          fullReportMarkdown: result.fullReportMarkdown,
          rolesUsed: result.rolesUsed,
          normativeReferences: result.normativeReferences,
          courtPrecedents: result.courtPrecedents
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || (isUk ? "Помилка відправки документа в Telegram" : "Failed to send to Telegram"));
      }

      setTgMdSuccess(true);
      setTimeout(() => setTgMdSuccess(false), 5000);
    } catch (err: any) {
      console.error("Telegram send error:", err);
      alert(err.message || (isUk ? "Помилка зв'язку з Telegram-ботом" : "Telegram error"));
    } finally {
      setSendingTgMd(false);
    }
  };

  // Ask clarifying follow-up question to mobilized specialists
  const handleAskFollowUp = async () => {
    if (!followUpQuestion.trim() || !result?.sessionId) return;
    setIsFollowUpLoading(true);

    try {
      const res = await fetch("/api/enterprise/universal-consultant/follow-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: result.sessionId,
          question: followUpQuestion.trim()
        })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || (isUk ? "Помилка відповіді колегії на уточнення" : "Follow-up failed"));
      }

      setFollowUps(prev => [
        ...prev,
        {
          question: data.question,
          answer: data.answer,
          rolesInvolved: data.rolesInvolved,
          timestamp: data.timestamp
        }
      ]);
      setFollowUpQuestion('');
    } catch (err: any) {
      console.error("Follow-up error:", err);
      alert(err.message || (isUk ? "Не вдалося отримати уточнення від колегії" : "Follow-up error"));
    } finally {
      setIsFollowUpLoading(false);
    }
  };

  // Load history of consultations from database
  const handleLoadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const res = await fetch("/api/enterprise/universal-consultant/history");
      if (res.ok) {
        const data = await res.json();
        setHistoryList(data || []);
      }
    } catch (err) {
      console.warn("Could not load history:", err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Restore past session from history
  const handleSelectHistorySession = async (id: string) => {
    try {
      const res = await fetch(`/api/enterprise/universal-consultant/history/${id}`);
      if (res.ok) {
        const session = await res.json();
        setResult({
          sessionId: session.id,
          docNumber: session.doc_number,
          docDate: session.doc_date,
          question: session.question,
          executiveSummary: session.executive_summary,
          rolesUsed: session.roles_used || [],
          specialistAnalyses: session.specialist_analyses || [],
          normativeReferences: session.normative_references || [],
          courtPrecedents: session.court_precedents || [],
          actionPlan: session.action_plan || [],
          riskMatrix: session.risk_matrix || [],
          fullReportMarkdown: session.full_report_markdown || '',
          docxUrl: session.docx_url || '',
          pdfUrl: session.pdf_url || '',
          modelUsed: session.model_used || 'gemini-3.7-flash',
          executionTimeMs: session.execution_time_ms || 0
        });
        setFollowUps(session.follow_ups || []);
        setIsHistoryOpen(false);
      }
    } catch (e) {
      console.error("Error loading past session:", e);
    }
  };

  const copyToClipboard = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.fullReportMarkdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Filtered ecosystem catalog items for modal search
  const filteredCatalogItems = useMemo(() => {
    if (!catalog) return [];
    let items: Array<{ id: string; name: string; category: string; description: string; type: 'platform_agent' | 'vault_prompt' }> = [];

    if (catalogTypeTab === 'all' || catalogTypeTab === 'platform_agent') {
      items = items.concat(catalog.agents.map(a => ({
        id: a.id,
        name: a.name,
        category: a.hub,
        description: a.description,
        type: 'platform_agent' as const
      })));
    }

    if (catalogTypeTab === 'all' || catalogTypeTab === 'vault_prompt') {
      items = items.concat(catalog.prompts.map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        description: p.description,
        type: 'vault_prompt' as const
      })));
    }

    const q = catalogSearch.toLowerCase().trim();
    if (q) {
      items = items.filter(item =>
        item.name.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.id.toLowerCase().includes(q)
      );
    }

    return items;
  }, [catalog, catalogSearch, catalogTypeTab]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 space-y-6">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2 hover:bg-slate-800 rounded-xl transition text-slate-400 hover:text-white cursor-pointer"
            title={isUk ? "Повернутися назад" : "Go Back"}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-amber-300" />
                НЦУВКЗ • Мультиагентний Оркестратор
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Gemini 3.7 Flash
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/20 text-blue-400 border border-blue-500/30">
                464 Спеціалісти (368 Агентів + 96 Промптів)
              </span>
            </div>
            <h1 className="text-xl md:text-2xl font-black text-white mt-1">
              {isUk ? "Універсальний Корпоративний Консультант-Оркестратор" : "Universal Corporate Consultant & Orchestrator"}
            </h1>
            <p className="text-xs text-slate-400">
              {isUk
                ? "Динамічний оркестратор усієї екосистеми платформи: мобілізація будь-яких необхідних ШІ-агентів і галузевих промптів під суть вашого завдання"
                : "Dynamic ecosystem orchestrator: mobilizes any required platform agents & expert prompts based on the inquiry"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setIsHistoryOpen(true);
              handleLoadHistory();
            }}
            className="text-xs text-amber-300 hover:text-white bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/40 px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 cursor-pointer"
            title={isUk ? "Історія та архів консультацій" : "Consultation History"}
          >
            <History className="w-3.5 h-3.5 text-amber-400" />
            <span>{isUk ? `Архів висновків (${historyList.length})` : `History (${historyList.length})`}</span>
          </button>
          <button
            type="button"
            onClick={() => setIsCatalogOpen(true)}
            className="text-xs text-indigo-300 hover:text-white bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/40 px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 cursor-pointer"
          >
            <Search className="w-3.5 h-3.5" />
            <span>{isUk ? "Каталог Екосистеми (464)" : "Browse Ecosystem (464)"}</span>
          </button>
          {onGoHome && (
            <button
              type="button"
              onClick={onGoHome}
              className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-xl border border-slate-800 hover:border-slate-700 transition"
            >
              {isUk ? "Головний Хаб" : "Main Hub"}
            </button>
          )}
        </div>
      </div>

      {/* Main Workspace Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Input Form & Specialist Controls */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 space-y-4 shadow-xl">
            {/* Question Textarea */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-bold text-white uppercase tracking-wider flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5 text-indigo-400" />
                  <span>{isUk ? "1. Запитання або фабула завдання:" : "1. Inquiry or Task Formulation:"}</span>
                </label>
                <span className="text-[10px] text-slate-400 font-mono">
                  {question.length} {isUk ? "симв." : "chars"}
                </span>
              </div>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={5}
                placeholder={
                  isUk
                    ? "Опишіть ситуацію, вимогу або проблему (наприклад: закупівля супутникових знімків ДЗЗ за КЕКВ, будівництво антени за АВК-5, розсекречення даних за Законом про держтаємницю, оформлення відрядження на сейсмостанцію ГЦСК, претензія за зрив строків)..."
                    : "Describe the task, challenge or required consultation..."
                }
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition resize-y font-sans leading-relaxed"
              />
            </div>

            {/* Presets Grid */}
            <div>
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                {isUk ? "Типові фабули НЦУВКЗ:" : "Common NCUVKZ Presets:"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PRESETS.map((preset, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setQuestion(preset.question)}
                    className="p-2.5 text-left rounded-xl bg-slate-950/60 hover:bg-indigo-950/30 border border-slate-800 hover:border-indigo-500/40 transition group cursor-pointer"
                  >
                    <div className="text-xs font-bold text-slate-200 group-hover:text-indigo-300 line-clamp-1">
                      {preset.titleUk}
                    </div>
                    <div className="text-[10px] text-slate-500 line-clamp-2 mt-0.5">
                      {preset.descUk}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* File Attachment Dropzone */}
            <div>
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-2">
                {isUk ? "2. Додати файли для аналізу (.docx, .xlsx, .pdf, .pptx, скани):" : "2. Attach input files:"}
              </label>
              <div className="relative border-2 border-dashed border-slate-800 hover:border-indigo-500/50 rounded-xl p-4 text-center transition bg-slate-950/40">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <Upload className="w-6 h-6 text-indigo-400 mx-auto mb-1.5" />
                <div className="text-xs font-medium text-slate-300">
                  {isUk ? "Натисніть або перетягніть файли сюди" : "Click or drag files here"}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Word, Excel, PDF, PowerPoint, тексти, скани
                </div>
              </div>

              {/* Attached files list */}
              {attachedFiles.length > 0 && (
                <div className="mt-2.5 space-y-1.5">
                  {attachedFiles.map((file, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 rounded-lg bg-slate-950 border border-slate-800 text-xs"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <FileText className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
                        <span className="truncate text-slate-200">{file.name}</span>
                        <span className="text-[10px] text-slate-500">
                          ({(file.size / 1024).toFixed(0)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(idx)}
                        className="p-1 text-slate-400 hover:text-red-400 transition"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Specialist Selector & Ecosystem Browser */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                  {isUk ? "3. Закликати фахівців (Екосистема 464 спеціалістів):" : "3. Specialist Fleet (464 Capabilities):"}
                </label>
                {selectedSpecialists.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSpecialists([])}
                    className="text-[10px] text-indigo-400 hover:underline cursor-pointer flex items-center gap-1"
                  >
                    <RefreshCw className="w-2.5 h-2.5" />
                    <span>{isUk ? "Скинути (ШІ-автопідбір)" : "Reset to Auto"}</span>
                  </button>
                )}
              </div>

              {/* Auto-matching indicator or Selected Pills */}
              <div className="p-3 rounded-xl bg-slate-950/80 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-slate-300 flex items-center gap-1.5 font-medium">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    {selectedSpecialists.length === 0
                      ? (isUk ? "ШІ-автопідбір за змістом запитання та файлів" : "AI auto-matching enabled")
                      : (isUk ? `Вибрано вручну: ${selectedSpecialists.length}` : `Manually selected: ${selectedSpecialists.length}`)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsCatalogOpen(true)}
                    className="py-1 px-2.5 rounded-lg bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 hover:text-white border border-indigo-500/40 text-[11px] font-bold transition flex items-center gap-1 cursor-pointer"
                  >
                    <Search className="w-3 h-3" />
                    <span>{isUk ? "Каталог Екосистеми (464)" : "Browse Ecosystem"}</span>
                  </button>
                </div>

                {/* Selected specialist chips */}
                {selectedSpecialists.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-slate-800/80">
                    {selectedSpecialists.map(id => {
                      const matchedItem = catalog
                        ? (catalog.agents.find(a => a.id === id) || catalog.prompts.find(p => p.id === id))
                        : AVAILABLE_SPECIALISTS.find(s => s.id === id);
                      const name = matchedItem ? (matchedItem as any).name || (matchedItem as any).labelUk : id;
                      const isAgent = catalog?.agents.some(a => a.id === id);
                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium bg-indigo-900/40 border border-indigo-500/40 text-indigo-200"
                        >
                          <span>{isAgent ? "🤖" : "📜"} {name}</span>
                          <button
                            type="button"
                            onClick={() => toggleSpecialist(id)}
                            className="hover:text-white text-indigo-400 cursor-pointer ml-0.5"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Quick-toggle core specialists */}
                <div className="flex flex-wrap gap-1 pt-1">
                  {AVAILABLE_SPECIALISTS.map(s => {
                    const isSelected = selectedSpecialists.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSpecialist(s.id)}
                        className={`px-2 py-1 rounded text-[10px] font-medium transition cursor-pointer ${
                          isSelected
                            ? "bg-indigo-600 text-white font-bold"
                            : "bg-slate-900 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                        }`}
                      >
                        {isUk ? s.labelUk : s.labelEn}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Options Checkbox */}
            <div className="pt-2 border-t border-slate-800/80">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-300 select-none">
                <input
                  type="checkbox"
                  checked={includeKnowledgeBase}
                  onChange={(e) => setIncludeKnowledgeBase(e.target.checked)}
                  className="rounded border-slate-700 text-indigo-600 focus:ring-indigo-500 bg-slate-950"
                />
                <span>
                  {isUk
                    ? "Враховувати внутрішню базу знань підприємства (регламенти, положення НЦУВКЗ)"
                    : "Include enterprise internal knowledge documents"}
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="button"
              disabled={isAnalyzing || !question.trim()}
              onClick={handleRunConsultation}
              className={`w-full py-3.5 px-4 rounded-xl font-black text-sm tracking-wide transition flex items-center justify-center gap-2 shadow-xl cursor-pointer ${
                isAnalyzing || !question.trim()
                  ? "bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700"
                  : "bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600 text-white shadow-indigo-900/40 border border-indigo-400/40 active:scale-[0.99]"
              }`}
            >
              {isAnalyzing ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin text-indigo-300" />
                  <span>{isUk ? "Мультиагентний аналіз триває..." : "Analyzing with multi-agents..."}</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>{isUk ? "🚀 Запустити Мультиагентну Експертизу" : "Run Multi-Agent Expert Consultation"}</span>
                </>
              )}
            </button>

            {error && (
              <div className="p-3 rounded-xl bg-red-950/50 border border-red-800/60 text-red-200 text-xs flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Output & Synthesis */}
        <div className="lg:col-span-7 space-y-5">
          {/* Loading Animation */}
          {isAnalyzing && (
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-8 text-center space-y-6 shadow-2xl">
              <div className="relative w-20 h-20 mx-auto">
                <div className="absolute inset-0 rounded-full border-4 border-indigo-500/20 animate-ping" />
                <div className="w-20 h-20 rounded-full border-4 border-t-indigo-500 border-r-blue-500 border-b-transparent border-l-transparent animate-spin" />
                <Sparkles className="w-8 h-8 text-indigo-400 absolute inset-0 m-auto" />
              </div>

              <div className="space-y-2">
                <h3 className="text-base font-black text-white">
                  {isUk ? "ШІ-Оркестратор формує колегіальний висновок..." : "Generating Multi-Agent Synthesis..."}
                </h3>
                <p className="text-xs text-indigo-300 font-mono animate-pulse min-h-[20px]">
                  {STEPS[loadingStep]}
                </p>
              </div>

              {/* Progress Steps */}
              <div className="max-w-md mx-auto space-y-2 text-left">
                {STEPS.map((step, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-2.5 text-xs transition-opacity ${
                      idx === loadingStep
                        ? "text-indigo-300 font-bold opacity-100"
                        : idx < loadingStep
                        ? "text-emerald-400 opacity-80"
                        : "text-slate-600 opacity-40"
                    }`}
                  >
                    {idx < loadingStep ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    ) : idx === loadingStep ? (
                      <RefreshCw className="w-3.5 h-3.5 text-indigo-400 animate-spin shrink-0" />
                    ) : (
                      <div className="w-3.5 h-3.5 rounded-full border border-slate-700 shrink-0" />
                    )}
                    <span className="truncate">{step}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consultation Result Presentation */}
          {result && !isAnalyzing && (
            <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-5 md:p-6 space-y-6 shadow-2xl">
              {/* Document Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-800 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-mono text-xs font-bold border border-indigo-500/30">
                      {result.docNumber}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">
                      {result.docDate}
                    </span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      ({(result.executionTimeMs / 1000).toFixed(1)}s • {result.modelUsed})
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-white mt-1">
                    {isUk ? "Експертний Висновок Мультиагентної Колегії" : "Multi-Agent Consensus Finding"}
                  </h2>
                </div>

                {/* Actions: Download Word, PDF, Sign, Copy */}
                <div className="flex flex-wrap items-center gap-2">
                  {(result.downloadDocxUrl || result.docxUrl) && (
                    <button
                      type="button"
                      disabled={downloadingDocx}
                      onClick={() => handleDownloadFile(result.downloadDocxUrl || result.docxUrl, `${result.docNumber || 'Експертний_Висновок'}.docx`, false)}
                      className="py-1.5 px-3 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-md cursor-pointer disabled:opacity-50"
                      title={isUk ? "Завантажити офіційний документ Word" : "Download Word"}
                    >
                      {downloadingDocx ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      <span>Word (.docx)</span>
                    </button>
                  )}

                  {(result.downloadPdfUrl || result.pdfUrl) && (
                    <button
                      type="button"
                      disabled={downloadingPdf}
                      onClick={() => handleDownloadFile(result.downloadPdfUrl || result.pdfUrl, `${result.docNumber || 'Експертний_Висновок'}.pdf`, true)}
                      className="py-1.5 px-3 bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-md cursor-pointer disabled:opacity-50"
                      title={isUk ? "Завантажити офіційний PDF документ" : "Download PDF"}
                    >
                      {downloadingPdf ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      <span>PDF (.pdf)</span>
                    </button>
                  )}

                  {/* Telegram .md Only Dispatcher (@Antigravity_cli_contabo_bot) */}
                  <button
                    type="button"
                    disabled={sendingTgMd}
                    onClick={handleSendTelegramMd}
                    className={`py-1.5 px-3 text-xs font-bold rounded-xl transition flex items-center gap-1.5 shadow-md cursor-pointer disabled:opacity-50 ${
                      tgMdSuccess
                        ? "bg-emerald-600 text-white border border-emerald-400"
                        : "bg-sky-600 hover:bg-sky-500 text-white border border-sky-400/40 shadow-sky-900/30"
                    }`}
                    title={isUk ? "Надіслати структурований звіт (.md) у Telegram-бот @Antigravity_cli_contabo_bot" : "Send markdown (.md) to Telegram"}
                  >
                    {sendingTgMd ? (
                      <RefreshCw className="w-3.5 h-3.5 animate-spin text-white" />
                    ) : tgMdSuccess ? (
                      <Check className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    <span>
                      {sendingTgMd
                        ? (isUk ? "Надсилається..." : "Sending...")
                        : tgMdSuccess
                        ? (isUk ? "Надіслано .md!" : "Sent .md!")
                        : (isUk ? "Telegram (.md)" : "Telegram (.md)")}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="p-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl transition border border-slate-700 cursor-pointer"
                    title={isUk ? "Копіювати звіт" : "Copy report"}
                  >
                    {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Semantic Cache Notice Banner */}
              {result.isCached && (
                <div className="p-3.5 rounded-xl bg-gradient-to-r from-emerald-950/60 via-slate-900 to-cyan-950/60 border border-emerald-500/40 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-md">
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 shrink-0">
                      <Zap className="w-4 h-4 animate-pulse" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-white flex items-center gap-2">
                        <span>{isUk ? "⚡ Отримано з семантичного кешу бази знань" : "⚡ Retrieved from Semantic Knowledge Cache"}</span>
                        {result.cacheSimilarity && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                            {isUk ? `Схожість ${result.cacheSimilarity}%` : `${result.cacheSimilarity}% match`}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 font-mono">
                          {result.executionTimeMs} ms
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-300 mt-0.5">
                        {isUk
                          ? "Аналогічне питання вже успішно опрацьоване колегією фахівців. Для отримання свіжого синтезу натисніть оновлення."
                          : "A highly similar inquiry was previously solved. Click refresh to force a full re-analysis."}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={isAnalyzing}
                    onClick={() => handleRunConsultation(true)}
                    className="py-1.5 px-3 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-600 hover:border-emerald-400/50 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shrink-0 self-start sm:self-center cursor-pointer disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isAnalyzing ? "animate-spin" : ""}`} />
                    <span>{isUk ? "Оновити без кешу" : "Bypass cache"}</span>
                  </button>
                </div>
              )}

              {/* Virtual Roundtable: Interactive Panel of Mobilized Experts */}
              <div className="p-4 rounded-2xl bg-gradient-to-br from-indigo-950/40 via-slate-950/80 to-slate-900 border border-indigo-500/30 space-y-3 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-indigo-500/20 pb-2.5">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-indigo-500/20 text-indigo-300">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <h3 className="text-xs font-black uppercase tracking-wider text-indigo-300 flex items-center gap-2">
                        <span>{isUk ? "🏛️ Віртуальний Круглий Стіл Колегії Фахівців" : "🏛️ Virtual Roundtable of Mobilized Specialists"}</span>
                        <span className="px-2 py-0.2 rounded-full text-[10px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
                          {result.rolesUsed.length} {isUk ? "експертів у засіданні" : "in session"}
                        </span>
                      </h3>
                      <p className="text-[11px] text-slate-400">
                        {isUk
                          ? "Натисніть на картку фахівця, щоб сфокусуватись на його компетенції або поставити йому пряме запитання нижче"
                          : "Click any expert card to focus and direct follow-up questions to them"}
                      </p>
                    </div>
                  </div>
                  {focusedSpecialistId && (
                    <button
                      type="button"
                      onClick={() => setFocusedSpecialistId(null)}
                      className="text-[11px] text-indigo-300 hover:text-white bg-indigo-900/40 hover:bg-indigo-900/60 px-2.5 py-1 rounded-lg border border-indigo-500/40 transition cursor-pointer self-start sm:self-center"
                    >
                      {isUk ? "Показати всю колегію" : "Show all experts"}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
                  {result.rolesUsed.map((role) => {
                    const isPlatformAgent = role.type === 'platform_agent';
                    const isFocused = focusedSpecialistId === role.id;
                    return (
                      <div
                        key={role.id}
                        onClick={() => {
                          if (isFocused) {
                            setFocusedSpecialistId(null);
                          } else {
                            setFocusedSpecialistId(role.id);
                            setFollowUpQuestion(prev => prev || (isUk ? `Питання до ${role.name}: ` : `Question for ${role.name}: `));
                          }
                        }}
                        className={`p-3 rounded-xl border transition cursor-pointer select-none text-left flex flex-col justify-between ${
                          isFocused
                            ? "bg-indigo-950/90 border-indigo-400 ring-2 ring-indigo-500/40 shadow-lg shadow-indigo-950"
                            : "bg-slate-900/80 border-slate-800 hover:border-indigo-500/40 hover:bg-slate-850"
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-1 mb-1.5">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                                isPlatformAgent
                                  ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                                  : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                              }`}
                            >
                              {isPlatformAgent ? "🤖 ШІ-Агент" : "📜 Промпт Vault"}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium truncate max-w-[120px]">
                              {role.category || role.domain}
                            </span>
                          </div>
                          <div className="text-xs font-bold text-white leading-tight">
                            {role.name}
                          </div>
                          <div className="text-[11px] text-slate-300 mt-1 italic line-clamp-2">
                            "{role.summary}"
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px] text-indigo-400 font-medium">
                          <span>{isFocused ? (isUk ? "✓ У фокусі колегії" : "✓ Active focus") : (isUk ? "Задати уточнення →" : "Direct question →")}</span>
                          <UserCheck className={`w-3.5 h-3.5 ${isFocused ? "text-emerald-400" : "text-slate-500"}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section I: Executive Summary */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-amber-400">
                  <CheckCircle className="w-4 h-4" />
                  <span>{isUk ? "І. Узагальнюючий висновок (Executive Summary)" : "I. Executive Summary"}</span>
                </div>
                <div className="p-4 rounded-xl bg-slate-950/80 border border-amber-500/20 text-slate-200 text-sm leading-relaxed whitespace-pre-line font-serif">
                  {result.executiveSummary}
                </div>
              </div>

              {/* Section II: Consulted Specialists Matrix */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-indigo-400">
                    <Layers className="w-4 h-4" />
                    <span>
                      {isUk
                        ? "ІІ. Мобілізована колегія спеціалістів екосистеми"
                        : "II. Mobilized Multi-Agent Panel"}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/15 text-indigo-300 border border-indigo-500/30">
                    {result.rolesUsed.length} {isUk ? "фахівців закличене" : "experts mobilized"}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {result.rolesUsed.map((role, idx) => {
                    const isPlatformAgent = role.type === 'platform_agent';
                    return (
                      <div
                        key={idx}
                        className="p-3 rounded-xl bg-slate-950/80 border border-slate-800/80 hover:border-slate-700 transition space-y-1.5"
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span
                            className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                              isPlatformAgent
                                ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                                : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                            }`}
                          >
                            {isPlatformAgent ? "🤖 ШІ-Агент" : "📜 Промпт Vault"}
                          </span>
                          <span className="text-[10px] text-slate-400 truncate max-w-[120px]">
                            {role.category || role.domain}
                          </span>
                        </div>
                        <div className="text-xs font-bold text-white line-clamp-1">
                          {role.name}
                        </div>
                        <div className="text-xs text-slate-300 pt-1 border-t border-slate-800/60 italic leading-snug">
                          "{role.summary}"
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Section III: Mandatory Clickable Statutory Links to zakon.rada.gov.ua */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-blue-400">
                    <ExternalLink className="w-4 h-4" />
                    <span>
                      {isUk
                        ? "ІІІ. Нормативно-правова база (Клікабельні посилання на zakon.rada.gov.ua)"
                        : "III. Statutory References (Direct links to Rada legislation)"}
                    </span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-500/15 text-blue-300 border border-blue-500/30">
                    {result.normativeReferences.length} {isUk ? "джерел" : "sources"}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-2.5">
                  {result.normativeReferences.map((ref, idx) => (
                    <div
                      key={idx}
                      className="p-3.5 rounded-xl bg-slate-950/80 border border-blue-500/20 hover:border-blue-500/50 transition flex flex-col md:flex-row md:items-center justify-between gap-3 group"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 font-bold text-[10px] border border-blue-500/30">
                            {ref.shortCode}
                          </span>
                          {ref.article && (
                            <span className="text-[11px] font-bold text-amber-300">
                              {ref.article}
                            </span>
                          )}
                          <span className="text-xs font-bold text-white group-hover:text-blue-200 transition">
                            {ref.title}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 leading-normal">
                          {ref.relevance}
                        </p>
                      </div>

                      <a
                        href={ref.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-1.5 px-3 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white border border-blue-500/40 hover:border-blue-400 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shrink-0 self-start md:self-center cursor-pointer"
                      >
                        <span>{isUk ? "Відкрити закон" : "Open law"}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section III-B: Supreme Court Precedents & Legal Positions (ЄДРСР) */}
              {result.courtPrecedents && result.courtPrecedents.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-purple-400">
                      <Scale className="w-4 h-4" />
                      <span>
                        {isUk
                          ? "ІІІ-Б. Судова практика Верховного Суду (ЄДРСР / Правові позиції)"
                          : "III-B. Supreme Court Precedents & Caselaw"}
                      </span>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500/15 text-purple-300 border border-purple-500/30">
                      {result.courtPrecedents.length} {isUk ? "позицій ВС" : "precedents"}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 gap-2.5">
                    {result.courtPrecedents.map((cp, idx) => (
                      <div
                        key={idx}
                        className="p-3.5 rounded-xl bg-slate-950/80 border border-purple-500/20 hover:border-purple-500/50 transition flex flex-col md:flex-row md:items-start justify-between gap-3 group"
                      >
                        <div className="space-y-1.5 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 font-bold text-[10px] border border-purple-500/30">
                              {cp.caseNumber}
                            </span>
                            {cp.proceedingNumber && (
                              <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 text-[10px] border border-slate-700">
                                {cp.proceedingNumber}
                              </span>
                            )}
                            <span className="text-xs font-bold text-white group-hover:text-purple-200 transition">
                              {cp.courtName}
                            </span>
                            {cp.decisionDate && (
                              <span className="text-[11px] text-slate-400">
                                • {cp.decisionDate}
                              </span>
                            )}
                          </div>

                          <div className="p-2.5 rounded-lg bg-slate-900/90 border border-slate-800 text-[11px] text-slate-200 leading-relaxed font-serif">
                            <span className="font-bold text-purple-300 mr-1.5">{isUk ? "Правова позиція:" : "Legal position:"}</span>
                            «{cp.legalPosition}»
                          </div>

                          {cp.relevance && (
                            <p className="text-[11px] text-slate-400 leading-normal">
                              <span className="text-amber-400/90 font-medium">{isUk ? "Практичне значення:" : "Significance:"}</span> {cp.relevance}
                            </p>
                          )}
                        </div>

                        <a
                          href={cp.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="py-1.5 px-3 bg-purple-600/20 hover:bg-purple-600 text-purple-300 hover:text-white border border-purple-500/40 hover:border-purple-400 rounded-lg text-xs font-bold transition flex items-center gap-1.5 shrink-0 self-start md:self-start cursor-pointer"
                        >
                          <span>{isUk ? "Відкрити в ЄДРСР" : "Open in Registry"}</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Section IV: In-Depth Specialist Analyses */}
              {result.specialistAnalyses && result.specialistAnalyses.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-emerald-400">
                    <Scale className="w-4 h-4" />
                    <span>{isUk ? "ІV. Детальні позиції профільних фахівців" : "IV. Specialist Details"}</span>
                  </div>
                  <div className="space-y-2">
                    {result.specialistAnalyses.map((sp, idx) => (
                      <details
                        key={idx}
                        className="group bg-slate-950/60 border border-slate-800 rounded-xl p-3 text-xs"
                      >
                        <summary className="font-bold text-slate-200 cursor-pointer flex items-center justify-between list-none">
                          <div className="flex items-center gap-2">
                            <span className="text-emerald-400 font-bold">•</span>
                            <span className="text-white">{sp.roleName}</span>
                            <span className="text-[10px] text-slate-500">({sp.domain})</span>
                          </div>
                          <span className="text-[10px] text-indigo-400 group-open:rotate-90 transition transform">
                            ▶
                          </span>
                        </summary>
                        <div className="mt-3 pt-3 border-t border-slate-800/80 space-y-2 text-slate-300 leading-relaxed font-serif">
                          <p>{sp.analysis}</p>
                          {sp.keyPoints && sp.keyPoints.length > 0 && (
                            <ul className="list-disc list-inside space-y-0.5 text-slate-400 pt-1 font-sans">
                              {sp.keyPoints.map((kp, kIdx) => (
                                <li key={kIdx}>{kp}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </details>
                    ))}
                  </div>
                </div>
              )}

              {/* Section V: Action Plan & Risk Matrix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Action Plan */}
                <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                  <div className="text-xs font-black uppercase tracking-wider text-indigo-400">
                    {isUk ? "Покроковий план дій для керівництва:" : "Action Plan:"}
                  </div>
                  <div className="space-y-1.5 text-xs text-slate-300">
                    {result.actionPlan.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="font-bold text-indigo-400 shrink-0">{idx + 1}.</span>
                        <span>{step}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Risk Matrix */}
                <div className="p-4 rounded-xl bg-slate-950/60 border border-slate-800 space-y-2">
                  <div className="text-xs font-black uppercase tracking-wider text-rose-400">
                    {isUk ? "Матриця ризиків та запобіжні заходи:" : "Risk Matrix:"}
                  </div>
                  <div className="space-y-2 text-xs">
                    {result.riskMatrix.map((rm, idx) => (
                      <div key={idx} className="p-2 rounded bg-slate-900/80 border border-slate-800">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-bold text-slate-200">{rm.risk}</span>
                          <span
                            className={`px-1.5 py-0.2 rounded text-[9px] font-black uppercase ${
                              rm.severity === 'high'
                                ? 'bg-red-500/20 text-red-400'
                                : rm.severity === 'medium'
                                ? 'bg-amber-500/20 text-amber-400'
                                : 'bg-emerald-500/20 text-emerald-400'
                            }`}
                          >
                            {rm.severity}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400">
                          <span className="text-slate-500 font-semibold">{isUk ? "Захід:" : "Mitigation:"} </span>
                          {rm.mitigation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Multi-Turn Follow-up Clarification Chat */}
              <div className="p-4 md:p-5 rounded-2xl bg-slate-950 border border-indigo-500/30 space-y-4 shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-indigo-500/20 text-indigo-400">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-xs font-black uppercase tracking-wider text-white">
                        {isUk ? "💬 Оперативні уточнення у колегії фахівців (Multi-Turn Dialogue)" : "💬 Specialist Panel Clarification"}
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        {isUk
                          ? "Поставте додаткове запитання за цим висновком — колегія надасть миттєве уточнення з урахуванням нормативів"
                          : "Ask clarifying questions on this finding — the panel responds with contextual citations"}
                      </p>
                    </div>
                  </div>
                  {followUps.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                      {followUps.length} {isUk ? "уточнень" : "dialogues"}
                    </span>
                  )}
                </div>

                {/* Follow-up Messages Thread */}
                {followUps.length > 0 && (
                  <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                    {followUps.map((fu, fIdx) => (
                      <div key={fIdx} className="space-y-2 p-3.5 rounded-xl bg-slate-900/90 border border-slate-800">
                        <div className="flex items-start gap-2 text-xs">
                          <span className="px-2 py-0.5 rounded bg-indigo-600/30 text-indigo-300 font-bold text-[10px] shrink-0 border border-indigo-500/40">
                            {isUk ? "Запитання" : "Question"} #{fIdx + 1}
                          </span>
                          <span className="font-semibold text-white">{fu.question}</span>
                        </div>

                        <div className="p-3 rounded-lg bg-slate-950/80 border border-indigo-950 text-xs text-slate-200 leading-relaxed font-serif whitespace-pre-line">
                          {fu.answer}
                        </div>

                        {fu.rolesInvolved && fu.rolesInvolved.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-[10px] text-slate-400">
                            <span className="font-medium text-slate-500">{isUk ? "Відповіли:" : "Answered by:"}</span>
                            {fu.rolesInvolved.map((rId, rIdx) => (
                              <span key={rIdx} className="px-1.5 py-0.2 rounded bg-indigo-950 text-indigo-300 border border-indigo-800/60 font-mono">
                                {rId}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Follow-up Question Input Form */}
                <div className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <textarea
                      value={followUpQuestion}
                      onChange={(e) => setFollowUpQuestion(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleAskFollowUp();
                        }
                      }}
                      rows={2}
                      placeholder={
                        focusedSpecialistId
                          ? (isUk ? `Задайте уточнююче запитання фахівцю (${focusedSpecialistId})...` : `Ask specific question to ${focusedSpecialistId}...`)
                          : (isUk ? "Задайте уточнююче запитання колегії (наприклад: 'А який конкретно КЕКВ застосовувати?' або 'Як захиститись від претензій ДАСУ?')..." : "Ask a clarifying follow-up question...")
                      }
                      className="flex-1 bg-slate-900 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition resize-none"
                    />
                    <button
                      type="button"
                      disabled={isFollowUpLoading || !followUpQuestion.trim()}
                      onClick={handleAskFollowUp}
                      className="px-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-600 text-white rounded-xl font-bold text-xs transition flex flex-col items-center justify-center gap-1 shadow-md cursor-pointer disabled:cursor-not-allowed shrink-0"
                    >
                      {isFollowUpLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin text-white" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      <span>{isUk ? "Запитати" : "Ask"}</span>
                    </button>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-500">
                    <span>Ctrl + Enter для швидкої відправки</span>
                    <span>Gemini 3.7 Flash • Колегіальне узгодження</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ecosystem Specialist Catalog Modal (Browse all 464 items) */}
      {isCatalogOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-4 md:p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                    Екосистема ШІ-Асистентів
                  </span>
                  <span className="text-xs text-slate-400">
                    {catalog ? `${catalog.totalCapabilities} доступних спеціалістів` : "Завантаження..."}
                  </span>
                </div>
                <h3 className="text-base font-black text-white mt-1">
                  {isUk ? "Каталог Спеціалістів: 368 ШІ-Агентів платформи • 96 Галузевих Промптів Vault" : "Specialist Fleet Catalog"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsCatalogOpen(false)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search and Tabs Filter */}
            <div className="p-4 border-b border-slate-800 space-y-3 bg-slate-950/40">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
                <input
                  type="text"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder={isUk ? "Пошук за назвою, сферою або ключовими словами (кошторис, ДЗЗ, закупівлі, ПДВ, КЕКВ, ТЗІ)..." : "Search specialists..."}
                  className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500"
                />
                {catalogSearch && (
                  <button
                    type="button"
                    onClick={() => setCatalogSearch('')}
                    className="absolute right-3 top-2.5 text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Type Tabs */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCatalogTypeTab('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    catalogTypeTab === 'all'
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-800/80 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {isUk ? "Всі (464)" : "All (464)"}
                </button>
                <button
                  type="button"
                  onClick={() => setCatalogTypeTab('platform_agent')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    catalogTypeTab === 'platform_agent'
                      ? "bg-cyan-600 text-white"
                      : "bg-slate-800/80 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <span>🤖 {isUk ? "ШІ-Агенти платформи (368)" : "Platform Agents (368)"}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCatalogTypeTab('vault_prompt')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                    catalogTypeTab === 'vault_prompt'
                      ? "bg-amber-600 text-white"
                      : "bg-slate-800/80 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  <span>📜 {isUk ? "Галузеві Промпти Vault (96)" : "Vault Prompts (96)"}</span>
                </button>
              </div>
            </div>

            {/* Scrollable Items List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              <div className="text-[11px] text-slate-400 mb-1">
                Знайдено: {filteredCatalogItems.length} спеціалістів. Натисніть на картку, щоб вибрати або зняти вибір.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {filteredCatalogItems.slice(0, 150).map(item => {
                  const isSelected = selectedSpecialists.includes(item.id);
                  const isAgent = item.type === 'platform_agent';
                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleSpecialist(item.id)}
                      className={`p-3 rounded-xl border transition cursor-pointer flex items-start gap-3 select-none ${
                        isSelected
                          ? "bg-indigo-950/60 border-indigo-500 shadow-md"
                          : "bg-slate-950/60 border-slate-800/80 hover:border-slate-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}} // handled by parent onClick
                        className="mt-0.5 rounded border-slate-700 text-indigo-600 pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span
                            className={`px-1.5 py-0.2 rounded text-[9px] font-black uppercase ${
                              isAgent
                                ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/30"
                                : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                            }`}
                          >
                            {isAgent ? "🤖 ШІ-Агент" : "📜 Промпт"}
                          </span>
                          <span className="text-[10px] text-slate-400 truncate">
                            {item.category}
                          </span>
                        </div>
                        <div className="text-xs font-bold text-white truncate">
                          {item.name}
                        </div>
                        <p className="text-[11px] text-slate-400 line-clamp-2 mt-0.5 leading-snug">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
              <div className="text-xs text-slate-300">
                {selectedSpecialists.length === 0 ? (
                  <span className="text-amber-400">✨ За замовчуванням діє автоматичний семантичний підбір ШІ</span>
                ) : (
                  <span>Вибрано: <strong className="text-white">{selectedSpecialists.length}</strong> спеціалістів</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedSpecialists.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedSpecialists([])}
                    className="px-3 py-1.5 rounded-xl border border-slate-700 text-xs text-slate-300 hover:text-white transition cursor-pointer"
                  >
                    {isUk ? "Скинути (Авто)" : "Reset to Auto"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setIsCatalogOpen(false)}
                  className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition shadow-md cursor-pointer"
                >
                  {isUk ? "Застосувати" : "Apply Selection"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Consultation Sessions History Modal */}
      {isHistoryOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Header */}
            <div className="p-4 md:p-5 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-400">
                  <History className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">
                    {isUk ? "Архів Корпоративних Висновків НЦУВКЗ" : "Consultation Sessions History"}
                  </h3>
                  <p className="text-xs text-slate-400">
                    {isUk ? "Збережені висновки мультиагентної колегії та діалоги уточнень" : "Saved consensus findings & follow-up dialogues"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsHistoryOpen(false)}
                className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
              {isLoadingHistory ? (
                <div className="p-8 text-center text-slate-400 flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin text-amber-400" />
                  <span>{isUk ? "Завантаження архіву..." : "Loading archive..."}</span>
                </div>
              ) : historyList.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-xs">
                  {isUk ? "В архіві поки немає збережених висновків. Згенеруйте першу консультацію!" : "No consultations found in history."}
                </div>
              ) : (
                historyList.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => handleSelectHistorySession(session.id)}
                    className="p-4 rounded-xl bg-slate-950/80 border border-slate-800 hover:border-amber-500/50 hover:bg-slate-900/90 transition cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 group"
                  >
                    <div className="space-y-1 min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 font-mono text-[11px] font-bold border border-indigo-500/30">
                          {session.doc_number}
                        </span>
                        <span className="text-[11px] text-slate-400">
                          {session.doc_date}
                        </span>
                        {session.created_at && (
                          <span className="text-[10px] text-slate-500">
                            {new Date(session.created_at).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-bold text-white group-hover:text-amber-300 transition line-clamp-2">
                        {session.question}
                      </div>
                      <div className="text-[11px] text-slate-400 line-clamp-1">
                        {session.executive_summary}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <span className="px-2 py-1 rounded bg-slate-800 text-slate-300 text-[11px] font-bold group-hover:bg-amber-600 group-hover:text-white transition">
                        {isUk ? "Відкрити →" : "Open →"}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-slate-950/60">
              <span className="text-xs text-slate-400">
                {isUk ? `Всього в архіві: ${historyList.length}` : `Total sessions: ${historyList.length}`}
              </span>
              <button
                type="button"
                onClick={() => setIsHistoryOpen(false)}
                className="px-4 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition cursor-pointer"
              >
                {isUk ? "Закрити" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
